import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiFailure, errorText } from "../api";
import { SettingsProvider } from "./settings";
import { LOGO, Message } from "./ui";

/** Nur Ziele innerhalb der Windows-App zulassen. */
function deskTarget(raw: string | null): string {
  return raw && raw.startsWith("/desktop") && !raw.startsWith("/desktop/anmelden") ? raw : "/desktop";
}

const POINTS: [string, string, string][] = [
  ["01", "Aufträge", "Jedes Projekt mit Ordnern, Fotos und Dokumenten."],
  ["02", "Nachweise", "Protokolle, Unterweisungen und Belehrungen, fertig als PDF."],
  ["03", "Termine", "Kalender, Notizen und Absprachen fürs ganze Team."],
];

/** Startansicht der Windows-App: eigene Anmeldung im Stil der App, nicht die Website. */
export function DesktopLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const target = deskTarget(params.get("weiter"));
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add("in-app", "in-desk");
    document.title = "HandVero";
    // Wer schon angemeldet ist, landet direkt in der App.
    api.get("/api/me").then(() => navigate(target, { replace: true }), () => undefined);
    return () => document.documentElement.classList.remove("in-app", "in-desk");
  }, [navigate, target]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const password = String(new FormData(e.currentTarget).get("password") ?? "");
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/auth/login", { email, password });
      navigate(target, { replace: true });
    } catch (err) {
      setError({ message: errorText(err), code: err instanceof ApiFailure ? err.code : undefined });
      setBusy(false);
    }
  }

  async function resend() {
    await api.post("/api/auth/resend-verification", { email }).catch(() => undefined);
    setResent(true);
  }

  const hour = new Date().getHours();
  const hello = hour < 11 ? "Guten Morgen." : hour < 18 ? "Willkommen." : "Guten Abend.";

  return (
    <SettingsProvider>
    <div className="hv-app dlogin">
      <aside className="dlogin-side">
        <div className="dside-logo">
          {LOGO}
          <span>
            <b>HandVero</b>
            <small>{window.location.hostname.startsWith("vorschau.") ? "Vorschau" : "Für Windows"}</small>
          </span>
        </div>
        <div className="dlogin-hello">
          <h1>{hello}</h1>
          <p>Alles zu Ihren Aufträgen an einem Ort, am PC und auf dem Handy.</p>
          <p className="dlogin-demo">Startphase: noch nicht vollständig verfügbar. Bitte sichern Sie Ihre Daten derzeit selbst.</p>
        </div>
        <ol className="dlogin-points">
          {POINTS.map(([n, title, text]) => (
            <li key={n}>
              <span>{n}</span>
              <div>
                <b>{title}</b>
                <small>{text}</small>
              </div>
            </li>
          ))}
        </ol>
      </aside>
      <main className="dlogin-main">
        <form className="app-form dlogin-form" onSubmit={submit} noValidate>
          <h2>Anmelden</h2>
          <label>
            E-Mail
            <input name="email" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            Passwort
            <input name="password" type="password" autoComplete="current-password" required />
          </label>
          {error && (
            <div className="dlogin-error">
              <Message>{error.message}</Message>
              {error.code === "not_verified" &&
                (resent ? (
                  <p className="muted-s">Falls ein unbestätigtes Konto zu {email} gehört, ist ein neuer Link unterwegs.</p>
                ) : (
                  <button type="button" className="btn-text" onClick={() => void resend()}>
                    Neuen Bestätigungslink senden
                  </button>
                ))}
            </div>
          )}
          <button className="app-btn" disabled={busy}>
            {busy ? "Anmelden …" : "Anmelden"}
          </button>
          <div className="dlogin-links">
            <Link to="/passwort-vergessen">Passwort vergessen?</Link>
            <span>
              Noch kein Konto? <Link to="/registrieren">Kostenlos registrieren</Link>
            </span>
          </div>
        </form>
      </main>
    </div>
    </SettingsProvider>
  );
}
